import { useTypedSelector } from "hooks";
import { Status } from "consts/transactions";
import { find } from "util/support";
import { selectAllMerchants } from "features/merchants/selectors";
import type { Merchant } from "features/merchants/types";
import {
    esTransactionsGroupByStatusQueryToken,
    topMerchantSucceededTransactionsByUniqueEmailQueryToken,
    useStatisticDashboardContext,
    useStatisticDashboardUtils
} from "ui/organizms/StatisticDashboard/StatisticDashboardProvider";
import type { TopMerchantSucceededTransactionsByUniqueEmailRecord } from "./types";

export default function useTopMerchants() {
    const merchantOf = find<Merchant>(
        useTypedSelector(selectAllMerchants),
        'coreId'
    );

    const statisticDashboard = useStatisticDashboardContext();

    const {
        payload: topMerchantSucceededTransactionsByUniqueEmailQueryResponse
    } = statisticDashboard[topMerchantSucceededTransactionsByUniqueEmailQueryToken];

    const isLoading = (
        statisticDashboard[topMerchantSucceededTransactionsByUniqueEmailQueryToken].isLoading ||
        statisticDashboard[esTransactionsGroupByStatusQueryToken].isLoading
    );

    const {
        getTransactionAmountPercentageByStatus
    } = useStatisticDashboardUtils();

    const getTopMerchantSucceededTransactionsByUniqueEmail = () =>
        Array.from(
            topMerchantSucceededTransactionsByUniqueEmailQueryResponse?.rows.reduce((topMerchantMap, row) => {
                const amountEuro = Number(row.at(0));
                const successfulTransactionsCount = Number(row.at(1));
                const coreId = String(row.at(2));

                return topMerchantMap.set(coreId, {
                    coreId,
                    name: String(merchantOf(coreId)?.fullName),
                    succeededTransactions: successfulTransactionsCount,
                    amount: amountEuro
                });
            }, new Map<string, TopMerchantSucceededTransactionsByUniqueEmailRecord>())
                .values() ?? []
        )
        .filter(({ name }) => isLoading || name);

    const getPercentageFromTotalAmount = (amount: number) => getTransactionAmountPercentageByStatus({
        status: Status.Succeeded,
        amount,
        rowParser: row => ({
            status: String(row.at(-1)),
            groupCount: Number(row.at(1))
        })
    });

    return {
        isLoading,
        getTopMerchantSucceededTransactionsByUniqueEmail,
        getPercentageFromTotalAmount
    };
};
